import { invariant } from '@epic-web/invariant';
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppExtraArgument, AppState } from '@src/providers/AppStateProvider/types';
import { globalToastManagerUI, logger, type CustomerQuote } from '@talos/kyoko';
import type { NavigateFunction } from 'react-router-dom';
import { getManualRFQPricingRoute } from '../../../containers/Routes/routes';
import {
  createDestroyKeyedStateAction,
  keyedActionWithInstanceID,
  sliceToKeyedReducer,
} from '../../../providers/AppStateProvider/keyedReducer';
import { streamingDataSlice } from '../../../providers/AppStateProvider/streamingDataSlice';
import { OMSSliceActions } from '../OMSSlice';
import { OMSView } from '../OMSView';
import {
  DEFAULT_MANUAL_RFQ_PRICING_FORM_KEY,
  getActiveFormKeyForCustomerQuote,
  getActiveManualRFQPricingFormHandler,
} from './activeForm';
import {
  addOTCRepoRFQPricingFormReducers,
  getInitialOTCRepoRFQPricingFormState,
  getOTCRepoRFQPricingFormActions,
} from './OTCRepoRFQPricingForm/OTCRepoRFQPricingFormState';
import {
  addRFQPricingFormReducers,
  getInitialRFQPricingFormState,
  getRFQPricingFormActions,
} from './RFQPricingForm/RFQPricingFormState';
import type { ManualRFQPricingState } from './types';

/** The drawer itself is a singleton. There is only one. Drawers are not created with the ID, so we hardcode the ID here and use that. */
export const DRAWER_RFQ_PRICING_INSTANCE_ID = 'drawer-rfq-pricing';

function getInitialInstanceState(): ManualRFQPricingState {
  return {
    activeFormKey: DEFAULT_MANUAL_RFQ_PRICING_FORM_KEY,
    form: getInitialRFQPricingFormState(),
    otcRepoForm: getInitialOTCRepoRFQPricingFormState(),
    isLoading: false,
    initialized: false,
    currentCustomerQuote: undefined,
    currentCustomerQuoteRFQID: undefined,
  };
}

const manualRFQPricingSliceInstance = createSlice({
  name: 'manualRFQPricing',
  initialState: getInitialInstanceState(),
  reducers: {
    setAddToast: (state, action: PayloadAction<ManualRFQPricingState['addToast']>) => {
      state.addToast = action.payload;
    },
    prime: (state, { payload: quote }: PayloadAction<CustomerQuote>) => {
      // We have a scoped addToast instance -- we might've been popped out. We can't just use the global toast manager.
      const addToast = state.addToast;

      // Reset the state, and carry over certain properties, for now just the addToast callback
      Object.assign(state, getInitialInstanceState(), { addToast });

      state.currentCustomerQuoteRFQID = quote.RFQID;
      state.currentCustomerQuote = quote;
      state.initialized = true;
      state.isLoading = false;
      state.activeFormKey = getActiveFormKeyForCustomerQuote(quote);

      getActiveManualRFQPricingFormHandler(state).prime(state, quote);
    },
    /** This is called by the ManualRFQPricingService. It keeps the primed CustomerQuote in sync with BE */
    setCurrentCustomerQuote: (state, { payload: cq }: PayloadAction<CustomerQuote>) => {
      state.currentCustomerQuote = cq;
      getActiveManualRFQPricingFormHandler(state).syncWithCustomerQuote(state, cq);
    },

    setIsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload;
    },
    touchAll: state => {
      getActiveManualRFQPricingFormHandler(state).touchAll(state);
    },

    initState: () => {
      return getInitialInstanceState();
    },
    /** Clean reset. Called on close of the form. */
    resetState: () => {
      return getInitialInstanceState();
    },
  },
  extraReducers: builder => {
    addRFQPricingFormReducers(builder);
    addOTCRepoRFQPricingFormReducers(builder);
  },
});

export const manualRFQPricingReducer = sliceToKeyedReducer(manualRFQPricingSliceInstance);

function getManualRFQPricingLifecycleActions(instanceID: string) {
  return {
    primeState: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.prime, instanceID),
    setCurrentCustomerQuote: keyedActionWithInstanceID(
      manualRFQPricingSliceInstance.actions.setCurrentCustomerQuote,
      instanceID
    ),
    setIsLoading: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.setIsLoading, instanceID),
    touchAll: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.touchAll, instanceID),
    resetState: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.resetState, instanceID),
    initState: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.initState, instanceID),
    destroyState: keyedActionWithInstanceID(
      createDestroyKeyedStateAction(manualRFQPricingSliceInstance.name),
      instanceID
    ),
    setAddToast: keyedActionWithInstanceID(manualRFQPricingSliceInstance.actions.setAddToast, instanceID),
  };
}

export function getRFQPricingActions(instanceID: string) {
  return {
    ...getManualRFQPricingLifecycleActions(instanceID),
    form: getRFQPricingFormActions(instanceID),
    otcRepoForm: getOTCRepoRFQPricingFormActions(instanceID),
  };
}

/**
 * To be used if you want to prime the ManualRFQPricingSlice with a Quote, but you only have the RFQID at hand.
 * The thunk will create a ws request to get the full CustomerQuote, and then using that response call the normal prime action.
 * Depending on the type of CustomerQuote, it will either navigate to a pricing panel or the pricing drawer, so don't worry about that bit.
 */
const primeRFQIDThunk = createAsyncThunk<
  void,
  { rfqID: string; navigate: NavigateFunction; layoutId: string },
  { state: AppState; extra: AppExtraArgument }
>('manualRFQPricingSlice/primeRFQIDThunk', async ({ rfqID, ...params }, thunk) => {
  try {
    const request = thunk.dispatch(streamingDataSlice.endpoints.getCustomerQuotes.initiate({ RFQID: rfqID }));
    const result = await request.unwrap();
    request.unsubscribe(); // now that we have the data, immediately unsubscribe. we do not need updates.

    const quote = result.get(rfqID);
    invariant(quote != null, 'No quote found');

    thunk.dispatch(primeThunk({ quote, ...params })); // hand the rest of the work over to the main priming thunk
  } catch (error) {
    logger.error(new Error(`Failed to fetch CustomerQuote for RFQID`), { extra: { rfqID } });
    globalToastManagerUI.addToast({
      text: 'Failed to find Quote',
      variant: 'NEGATIVE',
    });
    throw error; // Re-throw to let Redux Toolkit handle the rejected state properly
  }
});

/**
 * To be used if you want to prime the Manual RFQ Pricing form with some CustomerQuote. The thunk will also either
 * navigate to your pricing panel, or open the pricing drawer for you.
 */
const primeThunk = createAsyncThunk<
  void,
  { quote: CustomerQuote; navigate: NavigateFunction; layoutId: string },
  { state: AppState; extra: AppExtraArgument }
>('manualRFQPricingSlice/primeThunk', async ({ quote, navigate, layoutId }, thunk) => {
  if (quote.LegSummary) {
    const instanceID = quote.RFQID; // for panels, we are running with the RFQID as the state instanceID
    // prime the slice instance, and then navigate to the panel
    thunk.dispatch(getRFQPricingActions(instanceID).primeState(quote));
    navigate(getManualRFQPricingRoute({ rfqID: quote.RFQID, layoutId }));
  } else {
    // Else we are a non-compound CustomerQuote. For these, we use the singleton pricing drawer.
    // There is only one instance of the drawer, so the instanceID is a hardcoded string in the frontend.
    const instanceID = DRAWER_RFQ_PRICING_INSTANCE_ID;
    thunk.dispatch(getRFQPricingActions(instanceID).primeState(quote));
    thunk.dispatch(OMSSliceActions.openView(OMSView.ManualRFQPricingForm)); // instead of navigating to a panel, we just open the
  }
});

export const manualRFQPricingThunks = {
  primeRFQID: primeRFQIDThunk,
  prime: primeThunk,
};
